import { useCallback, useMemo, useRef, useState } from 'react';
import type { TOCNode } from '../types/DocumentTypes';
import { findRootChapter } from '../features/ContentRendering/utils/chapterUtils';
import { fetchHTMLRange } from '../features/ContentRendering/utils/htmlRangeUtils';
import { HTMLRange } from '../features/ContentRendering/types';
import { useSearchParams } from 'react-router';
import { scrollToPosition } from '../utils/ScrollUtils';
import { fetchChapterByPageNumber } from '../utils/DocumentUtils';
import {
  flattenChapters,
  isChapterLoadRequired,
  isValidTarget,
  TargetLocation,
  TargetLocationCandidate,
} from './ChapterNavigationUtils';

export interface UseChapterNavigationResult {
  state: NavigationState;
  navigateToChapter: (target: TargetLocationCandidate) => Promise<void>;
  currentChapter: TOCNode | null;
  htmlRange: HTMLRange | null;
  loadedSection: TOCNode | null;
}

interface UseChapterNavigationArgs {
  isbn: string;
  getAccessToken: () => Promise<string | undefined>;
  chapters: TOCNode[] | undefined;
  onNavigated?: () => void;
}

export type NavigationState = 'loading' | 'ready' | 'error';

export function useChapterNavigation({
  isbn,
  getAccessToken,
  chapters,
  onNavigated,
}: UseChapterNavigationArgs): UseChapterNavigationResult {
  const [state, setState] = useState<NavigationState>('loading');
  const [htmlRange, setHtmlRange] = useState<HTMLRange | null>(null);
  const [loadedSection, setLoadedSection] = useState<TOCNode | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentChapter, setCurrentChapter] = useState<TOCNode | null>(null);

  const navigationRequestIdRef = useRef(0);

  const flattenedChapters = useMemo(() => {
    return flattenChapters(chapters || []);
  }, [chapters]);

  /**
   * Determine the target from target candidate (= find chapter if it's missing
   * from target candidate).
   */
  const getTargetFromCandidate = useCallback(
    async (
      candidate: TargetLocationCandidate,
    ): Promise<TargetLocation | null> => {
      if (candidate.chapter) {
        return { chapter: candidate.chapter, pageNumber: candidate.pageNumber };
      }

      if (candidate.nodeId) {
        const chapter = flattenedChapters.find(
          (c) => c.nodeId === candidate.nodeId,
        );
        if (chapter) {
          return { chapter, pageNumber: candidate.pageNumber };
        }
      }

      if (candidate.pageNumber) {
        try {
          const positionChapters = await fetchChapterByPageNumber(
            isbn,
            candidate.pageNumber,
            await getAccessToken(),
          );

          return {
            chapter: positionChapters[0],
            pageNumber: candidate.pageNumber,
          };
        } catch (_error) {
          setState('error');
          return null;
        }
      }

      return null;
    },
    [getAccessToken, isbn, flattenedChapters],
  );

  /**
   * Update the search params in the URL to reflect the current chapter and page number.
   */
  const updateSearchParams = useCallback(
    (target: TargetLocation) => {
      const chapterId = target.chapter.nodeId.toString();
      const existingParams = Object.fromEntries(searchParams.entries());

      if (target.pageNumber) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { chapterId: _oldChapterId, ...rest } = existingParams;
        setSearchParams({
          ...rest,
          pageNumber: target.pageNumber,
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { pageNumber: _oldPageNumber, ...rest } = existingParams;
        setSearchParams({ ...rest, chapterId });
      }
    },
    [searchParams, setSearchParams],
  );

  /**
   * After a navigation is resolved, update the current chapter and loaded
   * section.
   */
  const handleResolvedNavigation = useCallback(
    (target: TargetLocation, loadedChapter: TOCNode) => {
      setCurrentChapter(target.chapter);
      setLoadedSection(loadedChapter);
      updateSearchParams(target);
    },
    [updateSearchParams],
  );

  /**
   * Scroll to the target, unless the target is the root chapter loaded.
   */
  const handleScrollToTarget = useCallback(
    async (target: TargetLocation, loadedChapter: TOCNode) => {
      /* If the target is the same as the loaded chapter and no other more
      specific target is provided, scroll to the top.
      Otherwise, scroll to the target. */
      const targetIsSameAsLoadedChapter =
        target.pageNumber === undefined &&
        target.chapter.nodeId === loadedChapter.nodeId;

      if (targetIsSameAsLoadedChapter) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        await scrollToPosition({
          blockIndex: target.chapter?.blockId,
          pageNumber: target.pageNumber,
        });
      }
    },
    [],
  );

  /**
   * Signal error only when the given request is still the latest navigation request.
   */
  const handleError = useCallback((requestId: number) => {
    if (navigationRequestIdRef.current === requestId) {
      setState('error');
    }
  }, []);

  /**
   * Handle loading the chapter content.
   */
  const loadContent = useCallback(
    async (chapter: TOCNode, requestId: number): Promise<void> => {
      const range = await fetchHTMLRange(
        isbn,
        chapter.blockId,
        await getAccessToken(),
      );
      if (navigationRequestIdRef.current !== requestId) {
        // A newer navigation started while we were loading, abort this one before we change html range or anything else
        return;
      }

      setHtmlRange(range);
    },
    [getAccessToken, isbn],
  );

  const navigateToChapter = useCallback(
    async (targetCandidate: TargetLocationCandidate): Promise<void> => {
      navigationRequestIdRef.current += 1;
      const requestId = navigationRequestIdRef.current;

      if (!chapters) {
        handleError(requestId);
        return;
      }

      const isValid = isValidTarget(targetCandidate);
      if (!isValid) {
        handleError(requestId);
        return;
      }

      const target = await getTargetFromCandidate(targetCandidate);
      if (!target) {
        handleError(requestId);
        return;
      }

      // Load the root chapter content for the target. We'll scroll to the target after loading.
      const rootChapter = findRootChapter(target.chapter, chapters);
      if (!rootChapter) {
        handleError(requestId);
        return;
      }

      // Check if we need to load new content
      const chapterLoadRequired = isChapterLoadRequired(
        currentChapter,
        rootChapter,
        chapters,
      );

      if (chapterLoadRequired) {
        setState('loading');
        try {
          await loadContent(rootChapter, requestId);
        } catch (_error) {
          handleError(requestId);
          return;
        }
      }

      if (navigationRequestIdRef.current !== requestId) {
        return;
      }

      // Handle the rest of the navigation process
      handleResolvedNavigation(target, rootChapter);
      setState('ready');

      // After everything is done, scroll to the target
      handleScrollToTarget(target, rootChapter);
      onNavigated?.();
    },
    [
      loadContent,
      handleScrollToTarget,
      onNavigated,
      chapters,
      getTargetFromCandidate,
      handleResolvedNavigation,
      handleError,
      currentChapter,
    ],
  );

  return useMemo(
    () => ({
      navigateToChapter,
      htmlRange,
      loadedSection,
      currentChapter,
      state,
    }),
    [navigateToChapter, htmlRange, loadedSection, currentChapter, state],
  );
}
